import React from 'react';
import {
    canUploadRecallFile,
    extractFileName,
    formatRecallStatusLabel,
    hasInitialReport,
    hasUploadedFile,
    RECALL_STATUS_META,
} from './recallsQuery';
import RecallInitialReportButton from './RecallInitialReportButton';
import RecallUploadButton from './RecallUploadButton';

function RecallStatusBadge({ status }) {
    const meta = RECALL_STATUS_META[status] || RECALL_STATUS_META.PENDING;

    return (
        <span className={`recall-status ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {formatRecallStatusLabel(status)}
        </span>
    );
}

function RecallRequestList({ records, clinicId, onViewDetails, onUploadComplete, loading = false }) {
    if (!records.length) {
        return (
            <div className="ecx-page-empty py-5">
                <span className="ecx-page-empty__icon">
                    <i className="bi bi-person-check" />
                </span>
                <h5 className="mb-2 text-dark fw-normal">No recall requests found</h5>
                <p className="mb-0 text-muted">
                    Submit a new recall request or adjust your filters to see existing batches.
                </p>
            </div>
        );
    }

    return (
        <div className="recall-request-list">
            {records.map((request) => {
                const isPending = request.status === 'PENDING';
                const fileName = extractFileName(request.filePath);

                return (
                    <div
                        key={request.id}
                        className={`recall-request-item${isPending ? ' recall-request-item--pending' : ''}`}
                    >
                        <div className="recall-request-item__main">
                            <div className="recall-request-item__title-row">
                                <h6 className="recall-request-item__title">{request.title}</h6>
                                <RecallStatusBadge status={request.status} />
                            </div>

                            {request.notes && (
                                <p className="recall-request-item__notes mb-0">{request.notes}</p>
                            )}

                            <div className="recall-request-item__meta">
                                <span>
                                    <i className="bi bi-calendar3 me-1"></i>
                                    Created {request.createdAt || '—'}
                                </span>
                                {request.modifiedAt && request.modifiedAt !== request.createdAt && (
                                    <span>
                                        <i className="bi bi-clock-history me-1"></i>
                                        Updated {request.modifiedAt}
                                    </span>
                                )}
                                {hasUploadedFile(request) ? (
                                    <span>
                                        <i className="bi bi-paperclip me-1"></i>
                                        {fileName}
                                    </span>
                                ) : (
                                    <span className="text-warning">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        No file uploaded
                                    </span>
                                )}
                            </div>

                            {request.adminNotes && (
                                <div className="recall-request-item__admin-notes">
                                    <span className="recall-request-item__admin-notes-label">Admin notes</span>
                                    <span>{request.adminNotes}</span>
                                </div>
                            )}
                        </div>

                        <div className="recall-request-item__actions">
                            {hasInitialReport(request) && (
                                <RecallInitialReportButton
                                    clinicId={clinicId}
                                    recallId={request.id}
                                    request={request}
                                    disabled={loading}
                                />
                            )}
                            {canUploadRecallFile(request) && (
                                <RecallUploadButton
                                    clinicId={clinicId}
                                    recallId={request.id}
                                    onUploaded={onUploadComplete}
                                    disabled={loading}
                                />
                            )}
                            <button
                                type="button"
                                className="btn btn-light-primary btn-sm rounded-pill"
                                onClick={() => onViewDetails(request)}
                                disabled={loading}
                                title="View details"
                            >
                                <i className="bi bi-eye me-1"></i>
                                Details
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default RecallRequestList;
